import { HolderOutlined } from '@ant-design/icons';
import { App, Button, Input, Listy, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FileTypeEnum } from '../../../enums/FileTypeEnum';
import { SettingTypeEnum } from '../../../enums/SettingTypeEnum';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import type File from '../../../interfaces/File';
import type TagInterface from '../../../interfaces/Tag';
import { useAppStore } from '../../../store/store';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import { createSortableList } from '../../../utils/createSortableList';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';

interface FileTagsFormProps {
  file: File;
  onSubmit: (tagIds: number[]) => void;
  onCancel: () => void;
}

const LIST_HEIGHT = 280;

/** Match TagsList row density: px-3.5 sides, tight vertical gap; no Listy splitters. */
const listStyles = {
  root: { height: '100%' },
  item: {
    borderBottom: 'none',
    paddingBlock: 2,
    paddingInline: 14,
  },
} as const;

const listShellClassName = 'border-gray-medium h-[280px] overflow-hidden rounded-lg border';

type TagLockKind = 'system' | 'denyUnassign' | 'denyAssign' | null;

const getTagLockKind = (
  tag: TagInterface,
  initialTagIds: number[],
  selected: boolean,
): TagLockKind => {
  if (tag.isSystemTag && initialTagIds.includes(tag.id)) return 'system';
  if (tag.denyUnassign && initialTagIds.includes(tag.id) && selected) return 'denyUnassign';
  if (tag.denyAssign && !initialTagIds.includes(tag.id) && !selected) return 'denyAssign';
  return null;
};

const matchesQuery = (tag: TagInterface, query: string): boolean => {
  if (!query.trim()) return true;
  return tag.title.toLowerCase().includes(query.trim().toLowerCase());
};

interface TagRowProps {
  tag: TagInterface;
  isDarkMode: boolean;
  locked: TagLockKind;
  lockedTooltip?: string;
  onActivate: () => void;
  ariaLabel: string;
  dragHandle?: React.ReactNode;
}

const TagRow = ({
  tag,
  isDarkMode,
  locked,
  lockedTooltip,
  onActivate,
  ariaLabel,
  dragHandle,
}: TagRowProps) => {
  const { t } = useTranslation();
  const isLocked = locked != null;

  return (
    <div className="flex w-full items-center gap-1">
      {dragHandle}
      <button
        type="button"
        className={clsx(
          'min-w-0 flex-1 border-0 bg-transparent p-0',
          isLocked ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        disabled={isLocked}
        aria-label={ariaLabel}
        onClick={() => {
          if (isLocked) return;
          onActivate();
        }}
      >
        <Tag
          style={getTagPresentationStyle(tag.color, { darkMode: isDarkMode })}
          className={clsx(
            'relative m-0! w-full transition-all duration-300',
            !tag.fileVisible &&
              'bg-diagonal-stripes pointer-events-none! opacity-50! mix-blend-multiply!',
            isLocked && 'opacity-50 grayscale',
          )}
        >
          {/* Match TagsList: spacer | centered truncated title | trailing icons */}
          <div className="flex w-full flex-row items-center justify-center lg:justify-between">
            <div />
            <span className="hidden max-w-28 text-center lg:block">
              <TruncatedTooltip>{tag.title}</TruncatedTooltip>
            </span>
            <span className="lg:hidden">
              <TruncatedTooltip>{tag.title?.slice(0, 1)}</TruncatedTooltip>
            </span>
            <div className="hidden flex-row items-center gap-1 lg:flex">
              {tag.isUsedForFileVisibility ? (
                <Tooltip title={t('common.tagIsUsedForFileVisibility')}>
                  <div>
                    <Icon name="Share2Icon" className="text-gray-dark h-3 w-3" />
                  </div>
                </Tooltip>
              ) : (
                <div className="w-1" />
              )}
              {tag.publicTag ? (
                <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
              ) : (
                <div className="w-1" />
              )}
              {isLocked && lockedTooltip ? (
                <Tooltip title={lockedTooltip}>
                  <div>
                    <Icon name="LockIcon" className="text-gray-dark h-3 w-3" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </Tag>
      </button>
    </div>
  );
};

/**
 * Assign tags to a file via dual Listy panels:
 * left = available (click to add), right = assigned (click to remove, drag to reorder).
 */
export const FileTagsForm: React.FC<FileTagsFormProps> = ({ file, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const tags = useAppStore((state) => state.tags);
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';

  const initialTagIds = useMemo(() => file?.tags?.map((tag) => tag.id) || [], [file?.tags]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialTagIds);
  const [availableQuery, setAvailableQuery] = useState('');
  const [assignedQuery, setAssignedQuery] = useState('');
  const [sortableEpoch, setSortableEpoch] = useState(0);

  const assignedListRef = useRef<HTMLDivElement>(null);
  const selectedTagIdsRef = useRef(selectedTagIds);
  const dragStartOrderRef = useRef<number[]>([]);

  useEffect(() => {
    selectedTagIdsRef.current = selectedTagIds;
  }, [selectedTagIds]);

  const tagsById = useMemo(() => {
    const map = new Map<number, TagInterface>();
    for (const tag of tags) map.set(tag.id, tag);
    return map;
  }, [tags]);

  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const availableCount = useMemo(
    () => tags.filter((tag) => !selectedSet.has(tag.id)).length,
    [tags, selectedSet],
  );

  const availableTags = useMemo(
    () => tags.filter((tag) => !selectedSet.has(tag.id) && matchesQuery(tag, availableQuery)),
    [tags, selectedSet, availableQuery],
  );

  const assignedTags = useMemo(() => {
    return selectedTagIds
      .map((id) => tagsById.get(id))
      .filter((tag): tag is TagInterface => Boolean(tag))
      .filter((tag) => matchesQuery(tag, assignedQuery));
  }, [selectedTagIds, tagsById, assignedQuery]);

  const assignedCount = selectedTagIds.length;
  const dragEnabled = !assignedQuery.trim();

  const lockTooltip = (kind: TagLockKind): string | undefined => {
    if (kind === 'system') return t('common.systemTagLocked');
    if (kind === 'denyUnassign') return t('common.tagRemovalRestricted');
    if (kind === 'denyAssign') return t('common.tagAdditionRestricted');
    return undefined;
  };

  const handleAdd = (tagId: number) => {
    const tag = tagsById.get(tagId);
    if (!tag) return;
    if (getTagLockKind(tag, initialTagIds, false)) return;
    if (selectedSet.has(tagId)) return;
    setSelectedTagIds((prev) => [...prev, tagId]);
  };

  const handleRemove = (tagId: number) => {
    const tag = tagsById.get(tagId);
    if (!tag) return;
    if (getTagLockKind(tag, initialTagIds, true)) return;
    setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
  };

  useEffect(() => {
    const el = assignedListRef.current;
    if (!el || !dragEnabled || assignedTags.length < 2) return;

    let ended = false;
    const sortable = createSortableList(el, {
      handle: '.tag-drag-handle',
      draggable: '[data-sortable-id]',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      fallbackClass: 'sortable-fallback',
      onStart: () => {
        dragStartOrderRef.current = selectedTagIdsRef.current.slice();
      },
      onEnd: () => {
        const order = Array.from(el.children)
          .filter((child): child is HTMLElement => child instanceof HTMLElement)
          .map((child) => Number(child.dataset.sortableId))
          .filter((id) => Number.isFinite(id));

        // Restore DOM before React reconciles (anti removeChild NotFoundError).
        ended = true;
        sortable.destroy();
        const byId = new Map<number, HTMLElement>();
        for (const child of Array.from(el.children)) {
          if (!(child instanceof HTMLElement)) continue;
          const id = Number(child.dataset.sortableId);
          if (Number.isFinite(id)) byId.set(id, child);
        }
        for (const child of Array.from(el.children)) {
          if (child instanceof HTMLElement && child.dataset.sortableId) child.remove();
        }
        for (const id of dragStartOrderRef.current) {
          const node = byId.get(id);
          if (node) el.appendChild(node);
        }

        if (order.length === 0 || order.join(',') === dragStartOrderRef.current.join(',')) {
          setSortableEpoch((n) => n + 1);
          return;
        }

        setSelectedTagIds(order);
        setSortableEpoch((n) => n + 1);
      },
    });

    return () => {
      if (!ended) sortable.destroy();
    };
  }, [assignedTags, dragEnabled, sortableEpoch]);

  const handleSubmit = () => {
    const isVisibilityTagId = (id: number): boolean =>
      Boolean(tagsById.get(id)?.isUsedForFileVisibility);

    const addedVisibilityTagIds = selectedTagIds.filter(
      (id) => isVisibilityTagId(id) && !initialTagIds.includes(id),
    );

    if (addedVisibilityTagIds.length > 0) {
      const tagsForMessage = addedVisibilityTagIds
        .map((id) => tagsById.get(id))
        .filter((tag): tag is TagInterface => Boolean(tag));

      const modalInstance = modal.confirm({
        title: t('modal.applyIsUsedForFileVisibilityTags.title'),
        content: (
          <Trans>
            {t('modal.applyIsUsedForFileVisibilityTags.description', {
              resource: tagsForMessage.map((tag) => tag.title).join(', '),
            })}
          </Trans>
        ),
        okText: t('common.applyTags'),
        onOk: () => {
          onSubmit(selectedTagIds);
        },
        onCancel: () => {
          modalInstance.destroy();
        },
        width: 500,
      });
    } else {
      onSubmit(selectedTagIds);
    }
  };

  const hasChanges = useMemo(() => {
    if (initialTagIds.length !== selectedTagIds.length) return true;
    return initialTagIds.some((id, index) => id !== selectedTagIds[index]);
  }, [initialTagIds, selectedTagIds]);

  const isFilePdfOrOffice = useMemo(() => {
    const type = file?.fileType;
    if (!type) return false;
    return [
      FileTypeEnum.Pdf,
      FileTypeEnum.Word,
      FileTypeEnum.Excel,
      FileTypeEnum.Powerpoint,
    ].includes(type);
  }, [file?.fileType]);

  const selectedTagsUsedInAiScrapingOrWatermark = useMemo(() => {
    const enums = new Set<SettingTypeEnum>();
    for (const tagId of selectedTagIds) {
      const tag = tagsById.get(tagId);
      if (!tag?.usedInSettings?.length) continue;
      if (tag.usedInSettings.includes(SettingTypeEnum.AiScraping)) {
        enums.add(SettingTypeEnum.AiScraping);
      }
      if (tag.usedInSettings.includes(SettingTypeEnum.WatermarkProtection)) {
        enums.add(SettingTypeEnum.WatermarkProtection);
      }
    }
    return Array.from(enums);
  }, [selectedTagIds, tagsById]);

  const showPdfOfficeWarning =
    !isFilePdfOrOffice && selectedTagsUsedInAiScrapingOrWatermark.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <section
          className="flex min-w-0 flex-col gap-2"
          aria-labelledby="file-tags-available-heading"
          aria-describedby="file-tags-available-hint"
        >
          <div id="file-tags-available-heading" className="text-sm font-medium">
            {t('modal.fileTagsForm.availableTags')} ({availableCount})
          </div>
          <p id="file-tags-available-hint" className="text-gray-dark m-0 text-xs">
            {t('modal.fileTagsForm.clickHintAdd')}
          </p>
          <Input
            placeholder={`${t('modal.fileTagsForm.searchAvailableTags')}...`}
            value={availableQuery}
            onChange={(e) => setAvailableQuery(e.target.value)}
            allowClear
            prefix={<Icon name="SearchIcon" className="text-gray-dark h-4 w-4" />}
            aria-label={t('modal.fileTagsForm.searchAvailableTags')}
          />
          <div className={listShellClassName}>
            {availableTags.length === 0 ? (
              <div
                role="status"
                className="text-gray-dark flex h-full items-center justify-center px-3.5 text-center text-sm"
              >
                {availableQuery ? t('common.noResults') : t('common.noTagsYet')}
              </div>
            ) : (
              <Listy
                items={availableTags}
                height={LIST_HEIGHT}
                virtual
                rowKey="id"
                styles={listStyles}
                itemRender={(tag) => {
                  const locked = getTagLockKind(tag, initialTagIds, false);
                  return (
                    <TagRow
                      tag={tag}
                      isDarkMode={isDarkMode}
                      locked={locked}
                      lockedTooltip={lockTooltip(locked)}
                      onActivate={() => handleAdd(tag.id)}
                      ariaLabel={t('modal.fileTagsForm.clickToAdd', { title: tag.title })}
                    />
                  );
                }}
              />
            )}
          </div>
        </section>

        <section
          className="flex min-w-0 flex-col gap-2"
          aria-labelledby="file-tags-assigned-heading"
          aria-describedby="file-tags-assigned-hint"
        >
          <div id="file-tags-assigned-heading" className="text-sm font-medium">
            {t('modal.fileTagsForm.assignedTags')} ({assignedCount})
          </div>
          <p id="file-tags-assigned-hint" className="text-gray-dark m-0 text-xs">
            {t('modal.fileTagsForm.clickHintRemove')}
          </p>
          <Input
            placeholder={`${t('modal.fileTagsForm.searchAssignedTags')}...`}
            value={assignedQuery}
            onChange={(e) => setAssignedQuery(e.target.value)}
            allowClear
            prefix={<Icon name="SearchIcon" className="text-gray-dark h-4 w-4" />}
            aria-label={t('modal.fileTagsForm.searchAssignedTags')}
          />
          <div className={listShellClassName}>
            {assignedTags.length === 0 ? (
              <div
                role="status"
                className="text-gray-dark flex h-full items-center justify-center px-3.5 text-center text-sm"
              >
                {assignedQuery ? t('common.noResults') : t('modal.fileTagsForm.noAssignedTags')}
              </div>
            ) : (
              <div
                ref={assignedListRef}
                data-tags-dnd
                data-testid="file-tags-assigned-sortable"
                className="h-full overflow-auto py-0.5"
                style={{ height: LIST_HEIGHT }}
              >
                {assignedTags.map((tag) => {
                  const locked = getTagLockKind(tag, initialTagIds, true);
                  return (
                    <div key={tag.id} data-sortable-id={tag.id} className="px-3.5 py-0.5">
                      <TagRow
                        tag={tag}
                        isDarkMode={isDarkMode}
                        locked={locked}
                        lockedTooltip={lockTooltip(locked)}
                        onActivate={() => handleRemove(tag.id)}
                        ariaLabel={t('modal.fileTagsForm.clickToRemove', { title: tag.title })}
                        dragHandle={
                          dragEnabled ? (
                            <Button
                              type="text"
                              size="small"
                              className="tag-drag-handle shrink-0 cursor-grab! px-1!"
                              icon={<HolderOutlined />}
                              aria-label={t('modal.fileTagsForm.dragToReorder', {
                                title: tag.title,
                              })}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span className="inline-block w-7 shrink-0" aria-hidden />
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {showPdfOfficeWarning && (
        <div className="text-red-dark flex flex-col gap-1 text-sm">
          {selectedTagsUsedInAiScrapingOrWatermark.includes(SettingTypeEnum.AiScraping) && (
            <span>{t('modal.fileTagsForm.aiScrapingOnlyPdfOffice')}</span>
          )}
          {selectedTagsUsedInAiScrapingOrWatermark.includes(
            SettingTypeEnum.WatermarkProtection,
          ) && <span>{t('modal.fileTagsForm.watermarkOnlyPdfOffice')}</span>}
        </div>
      )}

      <div className="border-gray-medium flex flex-row items-center justify-end gap-2 border-t pt-2">
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={handleSubmit} disabled={!hasChanges}>
          {t('common.applyTags')}
        </Button>
      </div>
    </div>
  );
};

export default FileTagsForm;
